import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Auth, Cache } from 'aws-amplify';
import { persistCache } from 'apollo-cache-persist';
import localForage from 'localforage';
import {
	ApolloProvider,
	ApolloClient,
	InMemoryCache,
	HttpLink,
	from,
} from '@apollo/client';
import { typePolicies } from './apolloCachePolicies.js'; // Import the cache policies
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import axios from 'axios';
import { downloadFromS3Signed, graphql } from 'src/_shared/services/utils.js';
import { isMobileShell } from 'src/_shared/shell.js';
import { routeShellToPortal } from 'src/_shared/services/shellRouting.js';
import { rememberCurrentLocation } from 'src/_shared/services/redirectAfterLogin.js';
import { awsApiKey } from './awsExports.js';
import DefaultSpinner from './DefaultSpinnerComponent.jsx';
// Set ForceProdEnv true to force prod backend in development. //
import { configMode } from './configMode.js';
import { decryptUsingAES256 } from './settings.js';

let { host } = window.location;
host = host.replace('www.', '');

if (
	host == 'heartlanddentalcareers.com' ||
	host == 'heartlandaffiliationreferrals.com'
) {
	window.location.href = `https://heartlanddentalreferrals.com/login`;
}

const awsMeta = awsApiKey;

export const refreshJwtToken = async (authType) => {
	try {
		if (authType === 'default') {
			const session = await Auth.currentSession();
			const token = session.getIdToken().getJwtToken();
			Cookies.set('jwt', token);
			return token;
		} else {
			try {
				const federatedToken = Cache.getItem('federatedToken');
				if (!federatedToken || !federatedToken.refresh_token) {
					console.log(
						'No federated token or refresh token found, redirecting to logout'
					);
					rememberCurrentLocation();
					window.location.href = `${window.location.origin}/logout`;
				}

				const refreshToken = federatedToken.refresh_token;
				const domain = Cache.getItem('federatedDomain');
				const region = Cache.getItem('federatedRegion');
				const clientId = Cache.getItem('federatedClientId');
				const clientSecret = Cache.getItem('federatedClientSecret');

				if (!domain || !region || !clientId || !clientSecret) {
					console.error('Missing required federation parameters');
					throw new Error('Missing federation parameters');
				}

				const scope =
					'aws.cognito.signin.user.admin+email+openid+phone+profile';
				const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=refresh_token&refresh_token=${refreshToken}&scope=${scope}`;
				const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;

				const response = await axios.post(url, body, {
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						Authorization:
							'Basic ' + window.btoa(clientId + ':' + clientSecret),
					},
				});

				const tokens = response.data;

				if (!tokens.refresh_token && federatedToken.refresh_token) {
					tokens.refresh_token = federatedToken.refresh_token;
				}

				Cache.setItem('federatedToken', tokens);

				const token = tokens.id_token;
				Cookies.set('jwt', token);

				const expiresAt = Date.now() + tokens.expires_in * 1000;
				localStorage.setItem('expiresAt', expiresAt.toString());

				return token;
			} catch (error) {
				console.error('Error refreshing federated token:', error);
				throw error;
			}
		}
	} catch (error) {
		console.log(error);
		// Don't auto-logout if SAML login is in progress
		if (!localStorage.getItem('samlLoginInProgress')) {
			rememberCurrentLocation();
			window.location.href = `${window.location.origin}/logout`;
		}
	}
};

const getJwtToken = async (authType) => {
	const REFRESH_BUFFER = 60 * 1000;
	let jwtToken = null;
	let expiresAt;

	try {
		// Check if SAML login is in progress but tokens aren't ready yet
		if (localStorage.getItem('samlLoginInProgress') && !Cookies.get('jwt')) {
			throw new Error('SAML login in progress, skipping token retrieval');
		}

		if (authType === 'saml') {
			// Saml login
			const federatedToken = Cache.getItem('federatedToken');
			expiresAt = localStorage.getItem('expiresAt');

			if (federatedToken && federatedToken.id_token) {
				jwtToken = federatedToken.id_token;
				Cookies.set('jwt', jwtToken);

				if (expiresAt) {
					const currentTime = Date.now();
					if (parseInt(expiresAt) - currentTime < REFRESH_BUFFER) {
						jwtToken = await refreshJwtToken('saml');
						Cookies.set('jwt', jwtToken);
					}
				}
			} else if (federatedToken && federatedToken.refresh_token) {
				jwtToken = await refreshJwtToken('saml');
				Cookies.set('jwt', jwtToken);
			} else {
				jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
			}
		} else {
			jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
		}
	} catch (error) {
		console.error('Error in token retrieval:', error);
		if (authType === 'saml' && !localStorage.getItem('samlLoginInProgress')) {
			try {
				jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
			} catch (defaultError) {
				console.error('Default auth also failed:', defaultError);
				throw defaultError;
			}
		} else {
			throw error;
		}
	}

	return { jwtToken, expiresAt };
};

const errorTracker = {
	failures: [],
	maxFailures: 10,
	timeWindow: 15000,

	addFailure() {
		const now = Date.now();
		this.failures.push(now);

		this.failures = this.failures.filter(
			(timestamp) => now - timestamp <= this.timeWindow
		);

		if (this.failures.length >= this.maxFailures) {
			console.error(
				`Apollo client: ${this.maxFailures} failures within ${this.timeWindow / 1000} seconds. Redirecting to logout.`
			);
			this.redirectToLogout();
		}
	},

	redirectToLogout() {
		this.failures = [];
		rememberCurrentLocation();
		window.location.href = `${window.location.origin}/logout`;
	},
};

const createClient = async (authType) => {
	const { url } = awsMeta;
	const authLink = setContext(async (_, { headers }) => {
		try {
			const currentTime = Date.now();
			let { jwtToken, expiresAt } = await getJwtToken(authType);
			if (!jwtToken) jwtToken = await refreshJwtToken(authType);
			if (expiresAt && parseInt(expiresAt) - currentTime < 0)
				jwtToken = await refreshJwtToken(authType);

			return {
				headers: {
					...headers,
					Authorization: jwtToken,
				},
			};
		} catch (error) {
			console.error('Error getting auth token:', error);
			if (!localStorage.getItem('samlLoginInProgress')) {
				// Expired SSO sessions end up here on first load of a deep link.
				// Keep the page so the login flow can return to it. (ERIN-8413)
				rememberCurrentLocation();
				window.location.href = `${window.location.origin}/logout`;
			}
			return { headers };
		}
	});

	const removeTypenameLink = removeTypenameFromVariables();

	const httpLink = new HttpLink({
		uri: url,
	});

	const errorLink = onError(
		({ graphQLErrors, networkError, operation, forward }) => {
			let shouldTrackError = false;

			if (graphQLErrors) {
				graphQLErrors.forEach(({ message, locations, path, extensions }) => {
					console.error(
						`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`
					);

					if (
						extensions?.code === 'UNAUTHENTICATED' ||
						extensions?.code === 'FORBIDDEN' ||
						message.toLowerCase().includes('unauthorized') ||
						message.toLowerCase().includes('authentication') ||
						message.toLowerCase().includes('token')
					) {
						shouldTrackError = true;
					}
				});
			}

			if (networkError) {
				console.error(`Network error: ${networkError}`);

				if (
					networkError.statusCode === 401 ||
					networkError.statusCode === 403 ||
					networkError.statusCode >= 500
				) {
					shouldTrackError = true;
				}
			}

			if (shouldTrackError) {
				errorTracker.addFailure();
			}
		}
	);

	const link = from([removeTypenameLink, errorLink, authLink, httpLink]);

	const cache = new InMemoryCache({});
	await persistCache({
		cache,
		storage: localForage,
	});
	return new ApolloClient({
		link,
		cache,
		assumeImmutableResults: false,
	});
};

export function WithApiProvider({ Component }) {
	const [client, setClient] = useState(null);
	const [company, setCompany] = useState(null);
	const [key, setKey] = useState(0);

	useEffect(() => {
		let isMounted = true;

		const initializeClient = async () => {
			let authType = localStorage.getItem('authType');
			if (!authType) {
				authType = 'default';
				localStorage.setItem('authType', authType);
			}

			try {
				awsMeta.url = decryptUsingAES256(
					process.env[`REACT_APP_${configMode}_APPSYNCENDPOINT`]
				);

				const newClient = await createClient(authType);
				// Only update state if component is still mounted
				if (isMounted) {
					setClient(newClient);
				}

				let companyData;
				try {
					companyData = await graphql({
						input: { host },
						query: 'getCompanyByHost',
					});
					if (isMounted) {
						setCompany(companyData);
					}
				} catch (error) {
					console.error(error);
					return;
				}

				// Inside a mobile shell, a company moved to the eAI portal is
				// routed there before the login page shows (ERIN-8561).
				if (
					isMobileShell() &&
					(await routeShellToPortal(companyData, (id) =>
						graphql({ input: { id }, query: 'getCompany' })
					))
				) {
					return;
				}

				const favicon = document.querySelector('#favicon');
				const favTitle = document.querySelector('#faviconTitle');
				switch (host) {
					case 'app.erinapp.com': {
						favTitle.innerHTML = 'ERIN | Employee Referral Platform';
						favicon.setAttribute('href', '/erin_square.png');

						break;
					}

					case 'referrals.aus.com':
					case 'referralstest.aus.com':
					case 'myallied.aus.com': {
						// Keep the tab title Allied configured in branding (today
						// "MyAllied"); only the icon is pinned to the bundled file.
						favTitle.innerHTML =
							companyData?.faviconTitle || 'Allied Referrals';
						favicon.setAttribute('href', '/alliedfavicon.ico');

						break;
					}

					case 'referrals.pierpoint.com': {
						favTitle.innerHTML = 'Pierpoint | Referral Portal';
						favicon.setAttribute('href', '/Pierpoint.png');

						break;
					}

					case 'app.refervets.org': {
						favTitle.innerHTML = 'Refervets | Veteran Referral Network';
						favicon.setAttribute('href', '/refervets.png');

						break;
					}

					case 'referafriend.seaworldentertainment.com': {
						favTitle.innerHTML = 'Join Me At SEA';
						favicon.setAttribute('href', '/seaworld-favicon.ico');

						break;
					}

					case 'qa.referafriend.seaworldentertainment.com': {
						favTitle.innerHTML = 'Join Me At SEA';
						favicon.setAttribute('href', '/seaworld-favicon.ico');

						break;
					}

					case 'connect.myhatchpad.com': {
						favTitle.innerHTML = 'Hatchpad Connect';
						favicon.setAttribute('href', '/hatchpad.ico');

						break;
					}

					case 'referrals.sunriseseniorliving.com': {
						favTitle.innerHTML = 'Sunrise Employee Referrals';
						favicon.setAttribute('href', '/sunrise-favicon.ico');

						break;
					}

					case 'pinteresttalenthub.com': {
						favTitle.innerHTML =
							'Pinterest Talent Hub | Employee Referral Portal';
						favicon.setAttribute('href', '/pinterest_favicon.png');

						break;
					}

					case 'referrals.primaryaimllc.com': {
						favTitle.innerHTML = 'Primary Aim Referrals';
						favicon.setAttribute('href', '/primary_aim.jpeg');

						break;
					}

					default: {
						favTitle.innerHTML = companyData?.faviconTitle
							? companyData?.faviconTitle
							: 'ERIN | Employee Referral Portal';
						if (companyData?.favicon?.key) {
							const s3url = await downloadFromS3Signed(
								companyData.favicon.key,
								companyData.favicon.bucket
							);
							favicon.setAttribute('href', s3url);
						} else {
							favicon.setAttribute('href', '/erin_square.png');
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		};

		initializeClient();

		return () => {
			isMounted = false;
		};
	}, [key]);

	const handleSignOut = async () => {
		try {
			// Clean up timers and listeners
			if (window.tokenRefreshTimer) {
				clearInterval(window.tokenRefreshTimer);
				window.tokenRefreshTimer = null;
			}

			if (window.visibilityChangeListener) {
				document.removeEventListener(
					'visibilitychange',
					window.visibilityChangeListener
				);
				window.visibilityChangeListener = null;
			}

			window.tokenRefreshFailCount = 0;

			// Reset error tracker
			errorTracker.failures = [];

			await Auth.signOut();
			localStorage.clear();
			// Wait for the persisted Apollo cache to clear so a page load that
			// follows the sign-out does not open IndexedDB mid-delete. A failed
			// clear must not stop the rest of the sign-out.
			// Bounded: a hung IndexedDB transaction neither resolves nor rejects,
			// and the hand-off that follows must not wait on it forever.
			await Promise.race([
				localForage.clear().catch(() => {}),
				new Promise((resolve) => setTimeout(resolve, 3000)),
			]);
			Cache.clear();
			Cookies.remove('jwt');
			client.clearStore();

			setKey((prevKey) => prevKey + 1); // To force reinit of apollo client
		} catch (error) {
			console.log('Error signing out', error);
		}
	};

	useEffect(() => {
		const bc = new BroadcastChannel('erin-user-sync');
		bc.onmessage = (event) => {
			if (event.data?.type === 'SIGN_OUT') {
				sessionStorage.setItem('remote-sign-out', 'true');
				window.location.href = `${window.location.origin}/logout`;
			}
		};

		const handleStorageChange = (event) => {
			if (event.key === 'support-login-original-user-id') {
				window.location.reload();
			}
		};

		window.addEventListener('storage', handleStorageChange);

		return () => {
			bc.close();
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	if (!client) {
		return (
			<div
				style={{ height: '100%', width: '100%', backgroundColor: '#1b365c' }}
			>
				<DefaultSpinner />
			</div>
		);
	}

	return (
		<ApolloProvider client={client}>
			<Component
				secrets={{
					GoogleAuthClientID:
						process.env[`REACT_APP_${configMode}_GOOGLEAUTHCLIENTID`],
				}}
				handleSignOut={handleSignOut}
			/>
		</ApolloProvider>
	);
}
